import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { FirestoreService } from "../../../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../../../api/firebase/references";
import { FormMatrixTableDataModel } from "../../../../../../../data-model/tablesData";
import { FormMatrixTableColumnKeys } from "../../../../../../../enums/tableColumnKeys";

export interface FormMatrixConfigItem {
  form_name: string;
  icd: boolean;
  dosage: boolean;
  dosage_instr: boolean;
  ev: boolean;
  daw: boolean;
  review_needed: boolean;
}

export interface FormMatrixConfig {
  id: string;
  data: FormMatrixConfigItem[];
}

const mapConfigItemToTableRow = (
  item: FormMatrixConfigItem,
  index: number,
): FormMatrixTableDataModel => {
  const rowId = `form-matrix-${index}`;
  return {
    [FormMatrixTableColumnKeys.FORM_NAME]: item.form_name,
    [FormMatrixTableColumnKeys.ICD]: {
      id: `${rowId}-icd`,
      defaultChecked: item.icd,
    },
    [FormMatrixTableColumnKeys.DOSAGE]: {
      id: `${rowId}-dosage`,
      defaultChecked: item.dosage,
    },
    [FormMatrixTableColumnKeys.DOSAGE_INSTR]: {
      id: `${rowId}-dosageInstr`,
      defaultChecked: item.dosage_instr,
    },
    [FormMatrixTableColumnKeys.EV]: {
      id: `${rowId}-ev`,
      defaultChecked: item.ev,
    },
    [FormMatrixTableColumnKeys.DAW]: {
      id: `${rowId}-daw`,
      defaultChecked: item.daw,
    },
    [FormMatrixTableColumnKeys.REVIEW_NEEDED]: {
      id: `${rowId}-reviewNeeded`,
      defaultChecked: item.review_needed,
    },
    [FormMatrixTableColumnKeys.DELETE]: item.form_name,
  };
};

const mapTableRowToConfigItem = (
  row: FormMatrixTableDataModel,
): FormMatrixConfigItem => ({
  form_name: row[FormMatrixTableColumnKeys.FORM_NAME],
  icd: row[FormMatrixTableColumnKeys.ICD]?.defaultChecked ?? false,
  dosage: row[FormMatrixTableColumnKeys.DOSAGE]?.defaultChecked ?? false,
  dosage_instr:
    row[FormMatrixTableColumnKeys.DOSAGE_INSTR]?.defaultChecked ?? false,
  ev: row[FormMatrixTableColumnKeys.EV]?.defaultChecked ?? false,
  daw: row[FormMatrixTableColumnKeys.DAW]?.defaultChecked ?? false,
  review_needed:
    row[FormMatrixTableColumnKeys.REVIEW_NEEDED]?.defaultChecked ?? false,
});

const FORM_MATRIX_COLLECTION = "configurations/pharmacy_order_updates/metadata";
const FORM_MATRIX_DOC_ID = "form_flags";

export const useFormMatrixData = () => {
  const [tableData, setTableData] = useState<FormMatrixTableDataModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFormMatrix = async (cancelled?: () => boolean) => {
    setLoading(true);
    setError(null);
    try {
      const config = (await FirestoreService.getDocument(
        FirestoreDocumentReference.flaggingAlgorithmFormMatrix(),
      )) as FormMatrixConfig | null;

      if (cancelled?.()) return;
      if (config?.data && Array.isArray(config.data)) {
        const rows = config.data.map(mapConfigItemToTableRow);
        setTableData(rows);
      } else {
        setTableData([]);
      }
    } catch (err) {
      if (cancelled?.()) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setTableData([]);
    } finally {
      if (!cancelled?.()) setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchFormMatrix(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, []);

  const saveNewFormMatrixItem = async (newItem: FormMatrixConfigItem) => {
    let config: FormMatrixConfig | null = null;
    try {
      config = (await FirestoreService.getDocument(
        FirestoreDocumentReference.flaggingAlgorithmFormMatrix(),
      )) as FormMatrixConfig | null;
    } catch {
      config = null;
    }

    const existingData =
      config?.data && Array.isArray(config.data) ? config.data : [];
    const newData = [...existingData, newItem];

    if (config) {
      await FirestoreService.updateDocument(
        FORM_MATRIX_COLLECTION,
        FORM_MATRIX_DOC_ID,
        { data: newData },
      );
    } else {
      await FirestoreService.setDocument(
        FORM_MATRIX_COLLECTION,
        FORM_MATRIX_DOC_ID,
        { data: newData },
      );
    }

    await fetchFormMatrix();
  };

  const saveFormMatrixData = async (data: FormMatrixConfigItem[]) => {
    let config: FormMatrixConfig | null = null;
    try {
      config = (await FirestoreService.getDocument(
        FirestoreDocumentReference.flaggingAlgorithmFormMatrix(),
      )) as FormMatrixConfig | null;
    } catch {
      config = null;
    }

    if (config) {
      await FirestoreService.updateDocument(
        FORM_MATRIX_COLLECTION,
        FORM_MATRIX_DOC_ID,
        { data },
      );
    } else {
      await FirestoreService.setDocument(
        FORM_MATRIX_COLLECTION,
        FORM_MATRIX_DOC_ID,
        { data },
      );
    }

    await fetchFormMatrix();
  };

  const updateFormMatrixToggle = async (
    rowIndex: number,
    field: keyof FormMatrixConfigItem,
    value: boolean,
  ) => {
    let config: FormMatrixConfig | null = null;
    try {
      config = (await FirestoreService.getDocument(
        FirestoreDocumentReference.flaggingAlgorithmFormMatrix(),
      )) as FormMatrixConfig | null;
    } catch {
      toast.error("Failed to update. Please try again.");
      return;
    }

    const data = config?.data && Array.isArray(config.data) ? config.data : [];
    if (rowIndex < 0 || rowIndex >= data.length) return;

    const updatedData = data.map((item, i) =>
      i === rowIndex ? { ...item, [field]: value } : item,
    );

    if (config) {
      await FirestoreService.updateDocument(
        FORM_MATRIX_COLLECTION,
        FORM_MATRIX_DOC_ID,
        { data: updatedData },
      );
    } else {
      await FirestoreService.setDocument(
        FORM_MATRIX_COLLECTION,
        FORM_MATRIX_DOC_ID,
        { data: updatedData },
      );
    }

    await fetchFormMatrix();
  };

  const deleteFormMatrixItem = async (formName: string) => {
    let config: FormMatrixConfig | null = null;
    try {
      config = (await FirestoreService.getDocument(
        FirestoreDocumentReference.flaggingAlgorithmFormMatrix(),
      )) as FormMatrixConfig | null;
    } catch {
      toast.error("Failed to delete. Please try again.");
      return;
    }

    const data = config?.data && Array.isArray(config.data) ? config.data : [];
    const updatedData = data.filter((item) => item.form_name !== formName);

    if (config) {
      await FirestoreService.updateDocument(
        FORM_MATRIX_COLLECTION,
        FORM_MATRIX_DOC_ID,
        { data: updatedData },
      );
    }

    await fetchFormMatrix();
  };

  return {
    tableData,
    loading,
    error,
    saveNewFormMatrixItem,
    saveFormMatrixData,
    updateFormMatrixToggle,
    deleteFormMatrixItem,
    mapTableRowToConfigItem,
  };
};
